import { Box, Alert, CircularProgress, Dialog } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import type { Photo, PhotoViewMode } from '../../../types/photo';
import { PHOTO_SLOTS } from '../../../types/photo';
import * as photoService from '../../../services/photoService';
import { PhotoToolbar } from './PhotoToolbar';
import { PhotoGallery } from './PhotoGallery';
import { BatchUploadZone } from './BatchUploadZone';
import { usePermissions } from '../../../hooks/usePermissions';

interface PhotosTabProps {
  workOrderDetailKey: string;
}

export const PhotosTab = ({ workOrderDetailKey }: PhotosTabProps) => {
  const { canEditTestData } = usePermissions();
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<PhotoViewMode>('medium');

  const fetchPhotos = useCallback(async () => {
    try {
      setError(null);
      const data = await photoService.getPhotos(workOrderDetailKey);
      setPhotos(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load photos');
    } finally {
      setLoading(false);
    }
  }, [workOrderDetailKey]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const handleUpload = useCallback(async (
    originalFile: File, croppedFile: File, stage: string, position: string,
    onProgress: (pct: number) => void,
  ) => {
    const { photoKey, originalUploadUrl, croppedUploadUrl } = await photoService.requestUploadUrl(
      workOrderDetailKey,
      { FileName: originalFile.name, FileSize: originalFile.size, MimeType: originalFile.type, Stage: stage, Position: position },
    );

    // Upload both in parallel
    await Promise.all([
      photoService.uploadToBlob(originalUploadUrl, originalFile),
      photoService.uploadToBlob(croppedUploadUrl, croppedFile, onProgress),
    ]);

    await photoService.confirmUpload(photoKey);
    await fetchPhotos();
  }, [workOrderDetailKey, fetchPhotos]);

  const handleSlotUpload = useCallback(async (
    file: File, stage: string, position: string,
    onStep?: (step: string, progress: number) => void,
  ) => {
    const SUB: Record<string, number> = { uploading: 10, processing: 50, saving: 85, done: 100 };
    await photoService.slotUpload(workOrderDetailKey, file, stage, position, 'average', (evt) => {
      onStep?.(evt.type, SUB[evt.type] ?? 0);
    });
    await fetchPhotos();
  }, [workOrderDetailKey, fetchPhotos]);

  const handleRecrop = useCallback(async (
    photoKey: string, croppedFile: File, onProgress: (pct: number) => void,
  ) => {
    const { uploadUrl } = await photoService.requestRecropUrl(photoKey);
    await photoService.uploadToBlob(uploadUrl, croppedFile, onProgress);
    await photoService.confirmRecrop(photoKey);
    await fetchPhotos();
  }, [fetchPhotos]);

  const handleDelete = useCallback(async (photoKey: string) => {
    await photoService.deletePhoto(photoKey);
    await fetchPhotos();
  }, [fetchPhotos]);

  const handleUpdateComment = useCallback(async (photoKey: string, comment: string | null) => {
    await photoService.updatePhoto(photoKey, { Comment: comment });
    await fetchPhotos();
  }, [fetchPhotos]);

  const handleDownloadAll = useCallback(async () => {
    await photoService.downloadZip(workOrderDetailKey);
  }, [workOrderDetailKey]);

  const filledCount = photos.filter((p) => p.UploadStatus === 'complete').length;
  const [guideOpen, setGuideOpen] = useState<'upload' | 'actions' | null>(null);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {canEditTestData && <BatchUploadZone workOrderDetailKey={workOrderDetailKey} onComplete={fetchPhotos} />}
      <PhotoToolbar
        viewMode={viewMode} onViewModeChange={setViewMode}
        filledCount={filledCount} totalSlots={PHOTO_SLOTS.length}
        onDownloadAll={handleDownloadAll} hasPhotos={filledCount > 0}
        onOpenUploadGuide={() => setGuideOpen('upload')}
        onOpenActionsGuide={() => setGuideOpen('actions')}
      />
      <PhotoGallery
        viewMode={viewMode} photos={photos}
        onUpload={handleUpload} onSlotUpload={handleSlotUpload}
        onRecrop={handleRecrop}
        onDelete={handleDelete} onUpdateComment={handleUpdateComment}
        readOnly={!canEditTestData}
      />

      {/* Guide dialogs */}
      <Dialog open={guideOpen === 'upload'} onClose={() => setGuideOpen(null)} maxWidth={false}
        PaperProps={{ sx: { width: '90vw', height: '85vh', maxWidth: 1100, p: 0, overflow: 'hidden', borderRadius: 3 } }}>
        <Box component="iframe" src="/PhotoUpload-Guide.html" sx={{ width: '100%', height: '100%', border: 'none' }} />
      </Dialog>
      <Dialog open={guideOpen === 'actions'} onClose={() => setGuideOpen(null)} maxWidth={false}
        PaperProps={{ sx: { width: '85vw', height: '80vh', maxWidth: 950, p: 0, overflow: 'hidden', borderRadius: 3 } }}>
        <Box component="iframe" src="/PhotoActions-Guide.html" sx={{ width: '100%', height: '100%', border: 'none' }} />
      </Dialog>
    </Box>
  );
};
